import { cn } from "@/lib/tailwind";
import React, { MouseEvent } from "react";
import { ISelectOption } from "./types";

export interface IMenuProps {
  open: boolean;
  triggerWidth?: number;
  value?: string | number;
  options: ISelectOption[];
  menuPosition: "bottom" | "top";
  menuRef: React.RefObject<HTMLUListElement>;
  menuProps?: React.HTMLAttributes<HTMLUListElement>;
  onSelect: (event: MouseEvent<HTMLLIElement>, value: string | number) => void;
  searchable?: boolean;
  searchQuery?: string;
  onSearchChange?: (q: string) => void;
  overlap?: boolean;
  noResultFoundLabel?: string;
}

export const Menu = ({
  open,
  value,
  options,
  menuRef,
  onSelect,
  menuProps,
  triggerWidth,
  menuPosition,
  searchable,
  searchQuery,
  onSearchChange,
  overlap = true,
  noResultFoundLabel,
}: IMenuProps) => {
  return (
    <ul
      ref={menuRef}
      role="listbox"
      tabIndex={-1}
      {...menuProps}
      style={{ width: triggerWidth || "100%" }}
      className={cn(
        overlap ? "absolute" : "relative",
        "z-[1] max-h-56 w-full overflow-y-auto transition-[opacity,transform,visibility,height] duration-200",
        menuPosition === "bottom"
          ? overlap
            ? "top-full mt-[3px]"
            : "mt-[3px]"
          : overlap
            ? "bottom-full mb-[3px]"
            : "mb-[3px]",
        open ? "border-2 border-black bg-white opacity-100" : "pointer-events-none",
        menuProps?.className,
      )}
    >
      {open && searchable && (
        <li className="sticky top-0 z-[2] bg-white px-3 py-2">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => onSearchChange?.(e.target.value)}
            placeholder="Search..."
            autoFocus
            className="w-full border border-gray-300 px-2 py-1 text-sm text-black focus:border-black focus:outline-none"
          />
        </li>
      )}
      {open && (
        <>
          {options.length > 0 ? (
            options.map((option, index) => (
              <li
                role="option"
                key={`${index}-${option.value}`}
                aria-selected={option.value === value}
                onClick={(e) => onSelect(e, option.value)}
                className={cn(
                  "px-4 py-2 text-sm text-black transition-[opacity,transform] duration-200 hover:bg-gray-100 hover:text-gray-800",
                  option.value === value && "bg-gray-50",
                  option.className,
                )}
              >
                {option.label}
              </li>
            ))
          ) : (
            <li
              className={cn(
                "flex px-4 py-2 text-sm text-black transition-[opacity,transform] duration-200 hover:bg-gray-100 hover:text-gray-800",
              )}
            >
              {noResultFoundLabel}
            </li>
          )}
        </>
      )}
    </ul>
  );
};
